<template>
  <el-container class="chat-page">
    <resizable-aside :default-width="260" :min-width="200" :max-width="500" storage-key="chat-aside-width">
      <div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <div class="search-wrap" ref="searchWrap">
          <el-input
            class="search-text"
            size="small"
            placeholder="搜索联系人、群聊、聊天记录"
            v-model="searchText"
            @focus="onSearchFocus"
            @keyup.enter.native="onSearchEnter">
            <i class="el-icon-search el-input__icon" slot="prefix"></i>
          </el-input>

          <div v-show="showSearchPopup" class="search-popup" :style="searchPopupStyle">
            <div v-if="searchLoading" class="search-tip">
              <i class="el-icon-loading"></i> 搜索中...
            </div>
            <div v-else-if="!keyword" class="search-tip">输入关键词后按回车开始搜索</div>

            <template v-else>
              <div v-if="hasSearched && contactResults.length === 0 && groupResults.length === 0 && messageResults.length === 0"
                class="search-tip">未找到相关内容</div>

              <div v-if="contactResults.length > 0" class="search-section">
                <div class="section-title">联系人</div>
                <div v-for="item in contactResults" :key="'c-' + item.id"
                  class="result-row chat-item-like" @click="openUser(item.id)">
                  <div class="chat-left">
                    <head-image :name="item.showName" :size="45" :url="item.headImage"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.showName)"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="groupResults.length > 0" class="search-section">
                <div class="section-title">群聊</div>
                <div v-for="item in groupResults" :key="'g-' + item.id"
                  class="result-row chat-item-like" @click="openGroup(item.id)">
                  <div class="chat-left">
                    <head-image :name="item.showName" :size="45"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.showName)"></div>
                    </div>
                    <div v-if="item.includeText" class="chat-content">
                      <div class="chat-content-text" v-html="highlightText(item.includeText)"></div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="messageResults.length > 0" class="search-section">
                <div class="section-title">聊天记录</div>
                <div v-for="(item, idx) in messageResults" :key="'m-' + idx"
                  class="result-row chat-item-like" @click="openMessage(item)">
                  <div class="chat-left">
                    <head-image :name="item.groupName" :size="45"></head-image>
                  </div>
                  <div class="chat-right">
                    <div class="chat-name">
                      <div class="chat-name-text" v-html="highlightText(item.groupName)"></div>
                    </div>
                    <div class="chat-content">
                      <div class="chat-content-text" v-html="highlightText(item.preview)"></div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="plus-wrap" ref="plusWrap">
          <button class="plus-btn" @click.stop="togglePlusMenu" title="更多操作">
            <i class="el-icon-plus"></i>
          </button>
          <div v-show="showPlusMenu" class="plus-menu">
            <div v-for="item in plusMenuItems" :key="item.key" class="plus-menu-item" @click="onPlusMenuSelect(item)">
              {{ item.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="chat-loading" v-if="loading" v-loading="true" :element-loading-text="$t('chat.receivingMsg')"
        element-loading-spinner="el-icon-loading" element-loading-background="#F9F9F9" element-loading-size="24">
      </div>
      <virtual-scroller class="scroll-box" :items="showConversations">
        <template v-slot="{ item }">
          <chat-item :conversation="item" @click.native="onActiveItem(item)" @delete="onDelItem(item)"
            @info="onShowInfo(item)" @dnd="onDnd(item)" @top="onTop(item)" :active="item === activeConv"></chat-item>
        </template>
      </virtual-scroller>
    </resizable-aside>
    <el-container class="chat-box">
      <chat-box v-if="activeConv && !isSystem(activeConv)" :conversation="activeConv"></chat-box>
      <chat-system-box v-if="activeConv && isSystem(activeConv)" :conversation="activeConv"></chat-system-box>
    </el-container>

    <!-- 添加好友 -->
    <add-friend :dialogVisible="showAddFriend" @close="showAddFriend = false"></add-friend>

    <!-- 加入群聊 -->
    <el-dialog title="加入群聊" :visible.sync="showAddGroup" width="480px" :close-on-click-modal="false"
      @closed="onAddGroupClosed">
      <div class="add-group-search">
        <el-input placeholder="输入群号精确搜索" v-model="addGroupText" size="small"
          @keyup.enter.native="onAddGroupSearch" clearable>
          <el-button slot="append" icon="el-icon-search" @click="onAddGroupSearch"></el-button>
        </el-input>
      </div>
      <div v-if="addGroupResults.length > 0" class="add-group-results">
        <div v-for="group in addGroupResults" :key="group.id" class="add-group-item">
          <head-image :name="group.name" :url="group.headImageThumb" :size="44"></head-image>
          <div class="add-group-info">
            <div class="add-group-name">{{ group.name }}</div>
            <div v-if="group.groupNo" class="add-group-no">群号 {{ group.groupNo }}</div>
          </div>
          <div class="add-group-action">
            <span v-if="group.joined" class="status-tip">已加入</span>
            <span v-else-if="group.applied" class="status-tip">申请中</span>
            <el-button v-else type="primary" size="mini" @click="onApplyJoinGroup(group)">申请加入</el-button>
          </div>
        </div>
      </div>
      <div v-else-if="addGroupNoData" class="add-group-empty">未找到群号「{{ addGroupQuery }}」</div>
    </el-dialog>

    <!-- 聊天链接二维码 -->
    <el-dialog
      :visible.sync="showChatLink"
      width="360px"
      custom-class="chat-link-dialog"
      :show-close="false"
      @opened="renderChatLinkQr">
      <div class="chat-link-card">
        <div class="clc-body">
          <div class="clc-qr-wrap">
            <canvas ref="chatLinkCanvas"></canvas>
          </div>
          <div class="clc-actions">
            <button class="clc-btn outline" @click="onSaveChatLinkQr">保存图片</button>
            <button class="clc-btn primary" @click="onCopyChatLink">复制链接</button>
          </div>
        </div>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import QRCode from 'qrcode';
import ChatItem from "../components/chat/ChatItem.vue";
import ChatBox from "../components/chat/ChatBox.vue";
import ChatSystemBox from "../components/chat/ChatSystemBox.vue";
import ResizableAside from "../components/common/ResizableAside.vue";
import VirtualScroller from "../components/common/VirtualScroller.vue";
import AddFriend from "../components/friend/AddFriend.vue";
import HeadImage from "../components/common/HeadImage.vue";

export default {
  name: "chat",
  components: {
    ChatItem,
    ChatBox,
    ChatSystemBox,
    ResizableAside,
    VirtualScroller,
    AddFriend,
    HeadImage,
  },
  data() {
    return {
      searchText: "",
      searchKeyword: "",
      searchLoading: false,
      hasSearched: false,
      showSearchPopup: false,
      searchPopupStyle: {},
      messageContent: "",
      group: {},
      groupMembers: [],
      messageResults: [],
      // plus menu
      showPlusMenu: false,
      // 添加好友
      showAddFriend: false,
      // 加入群聊
      showAddGroup: false,
      addGroupText: "",
      addGroupQuery: "",
      addGroupResults: [],
      addGroupNoData: false,
      // 聊天链接
      showChatLink: false,
      chatLinkUrl: "",
    }
  },
  mounted() {
    document.addEventListener("click", this.onGlobalClick);
    window.addEventListener("resize", this.updateSearchPopupPosition);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onGlobalClick);
    window.removeEventListener("resize", this.updateSearchPopupPosition);
  },
  watch: {
    showSearchPopup(val) {
      if (val) {
        this.$nextTick(() => this.updateSearchPopupPosition());
      }
    }
  },
  methods: {
    onActiveItem(conv) {
      this.chatStore.setActive(conv.key);
    },
    onDelItem(conv) {
      this.$confirm(this.$t('chat.deleteChat', { name: conv.showName }), this.$t('chat.deleteChatTitle'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(async () => {
        if (this.isPrivate(conv) || this.isGroup(conv)) {
          const data = { chatId: conv.targetId }
          const chatTypeText = this.isPrivate(conv) ? "private" : "group";
          await this.$http({
            url: `/message/${chatTypeText}/deleteChat`,
            method: 'delete',
            data: data
          });
        }
        await this.chatStore.remove(conv.key);
      });
    },
    onShowInfo(conv) {
      if (this.isPrivate(conv)) {
        this.$router.push("/home/friend?id=" + conv.targetId);
      } else if (this.isGroup(conv)) {
        if (!this.groupStore.isGroup(conv.targetId)) {
          this.$message.error(this.$t('chat.notInGroup'))
          return;
        }
        this.$router.push("/home/group?id=" + conv.targetId);
      }
    },
    onDnd(conv) {
      if (this.isPrivate(conv)) {
        this.setFriendDnd(conv, conv.targetId, !conv.isDnd)
      } else {
        this.setGroupDnd(conv, conv.targetId, !conv.isDnd)
      }
    },
    setFriendDnd(conv, friendId, isDnd) {
      this.$http({ url: '/friend/dnd', method: 'put', data: { friendId, isDnd } }).then(() => {
        this.friendStore.setDnd(friendId, isDnd);
        this.chatStore.setDnd(conv.key, isDnd);
      });
    },
    setGroupDnd(conv, groupId, isDnd) {
      this.$http({ url: '/group/dnd', method: 'put', data: { groupId, isDnd } }).then(() => {
        this.groupStore.setDnd(groupId, isDnd);
        this.chatStore.setDnd(conv.key, isDnd);
      });
    },
    onTop(conv) {
      if (this.isPrivate(conv)) {
        this.setFriendTop(conv, conv.targetId, !conv.isTop)
      } else {
        this.setGroupTop(conv, conv.targetId, !conv.isTop)
      }
    },
    setFriendTop(conv, friendId, isTop) {
      this.$http({ url: '/friend/top', method: 'put', data: { friendId, isTop } }).then(() => {
        this.friendStore.setTop(friendId, isTop);
        this.chatStore.setTop(conv.key, isTop);
      });
    },
    setGroupTop(conv, groupId, isTop) {
      this.$http({ url: '/group/top', method: 'put', data: { groupId, isTop } }).then(() => {
        this.groupStore.setTop(groupId, isTop);
        this.chatStore.setTop(conv.key, isTop);
      });
    },
    isPrivate(conv) {
      return this.$enums.CONVERSATION_TYPE.PRIVATE == conv.type;
    },
    isGroup(conv) {
      return this.$enums.CONVERSATION_TYPE.GROUP == conv.type;
    },
    isSystem(conv) {
      return this.$enums.CONVERSATION_TYPE.SYSTEM == conv.type;
    },
    // 搜索相关
    updateSearchPopupPosition() {
      if (!this.showSearchPopup) return;
      const wrap = this.$refs.searchWrap;
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      const width = Math.min(Math.round(rect.width + 120), 460);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      this.searchPopupStyle = {
        top: `${rect.bottom + 4}px`,
        left: `${left}px`,
        width: `${width}px`
      };
    },
    onSearchFocus() {
      this.showSearchPopup = true;
      this.preloadGroupMembers();
      this.$nextTick(() => this.updateSearchPopupPosition());
    },
    async onSearchEnter() {
      this.showSearchPopup = true;
      this.preloadGroupMembers();
      const next = String(this.searchText || "").trim();
      if (!next) {
        this.searchKeyword = "";
        this.searchLoading = false;
        this.hasSearched = false;
        this.messageResults = [];
        return;
      }
      this.searchLoading = true;
      this.hasSearched = false;
      this.messageResults = [];
      await this.$nextTick();
      this.updateSearchPopupPosition();
      await this.waitForPaint();
      this.searchKeyword = next;
      await this.searchMessagesByKeyword(next);
      this.searchLoading = false;
      this.hasSearched = true;
      this.searchText = "";
    },
    onGlobalClick(e) {
      const searchWrap = this.$refs.searchWrap;
      if (searchWrap && !searchWrap.contains(e.target)) {
        this.showSearchPopup = false;
        this.clearSearchState();
      }
      const plusWrap = this.$refs.plusWrap;
      if (plusWrap && !plusWrap.contains(e.target)) {
        this.showPlusMenu = false;
      }
    },
    clearSearchState() {
      this.searchText = "";
      this.searchKeyword = "";
      this.searchLoading = false;
      this.hasSearched = false;
      this.messageResults = [];
    },
    waitForPaint() {
      return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    },
    preloadGroupMembers() {
      this.groupStore.groups.filter(g => !g.quit).forEach(g => {
        if (!Array.isArray(g.members) || g.members.length === 0) {
          this.groupStore.refreshMember(g.id);
        }
      });
    },
    groupMatchInfo(group, keyword) {
      if (!group || !keyword) return { matched: false, includeText: "" };
      const key = String(keyword).toLowerCase();
      if (String(group.showGroupName || "").toLowerCase().includes(key)) {
        return { matched: true, includeText: "" };
      }
      const members = Array.isArray(group.members) ? group.members : [];
      for (const m of members) {
        const showName = String(m && m.showNickName || "").toLowerCase();
        const nickName = String(m && m.nickName || "").toLowerCase();
        if (showName.includes(key) || nickName.includes(key)) {
          return { matched: true, includeText: `包含：${m.showNickName || m.nickName || ""}` };
        }
        const friend = m && m.userId ? this.friendStore.findFriend(m.userId) : null;
        const remarkName = String(friend && friend.remarkNickName || "").toLowerCase();
        const friendShowName = String(friend && friend.showNickName || "").toLowerCase();
        if (remarkName.includes(key) || friendShowName.includes(key)) {
          const displayName = String(
            (friend && (friend.remarkNickName || friend.showNickName || friend.nickName)) ||
            (m && (m.showNickName || m.nickName)) || ""
          );
          return { matched: true, includeText: `包含：${displayName}` };
        }
      }
      return { matched: false, includeText: "" };
    },
    messageMatchesKeyword(msg, keyword) {
      if (!msg || msg.type === undefined || !keyword) return false;
      const key = String(keyword).toLowerCase();
      if (this.$enums.MESSAGE_TYPE.TEXT == msg.type) {
        return String(msg.content || "").toLowerCase().includes(key);
      }
      if (this.$enums.MESSAGE_TYPE.FILE == msg.type) {
        try {
          const data = typeof msg.content === "object" ? msg.content : JSON.parse(msg.content || "{}");
          return String((data && data.name) || "").toLowerCase().includes(key);
        } catch (e) { return false; }
      }
      if (this.$enums.MESSAGE_TYPE.RICH_TEXT == msg.type || this.$enums.MESSAGE_TYPE.MEDIA_ALBUM == msg.type) {
        try {
          const data = typeof msg.content === "object" ? msg.content : JSON.parse(msg.content || "{}");
          if (data && Array.isArray(data.items) && data.items.length) {
            return String(data.caption || "").toLowerCase().includes(key);
          }
          return (Array.isArray(data.blocks) ? data.blocks : [])
            .some(b => b && b.type === "text" && String(b.content || "").toLowerCase().includes(key));
        } catch (e) { return false; }
      }
      return false;
    },
    getSearchableText(msg) {
      if (!msg || msg.type === undefined) return "";
      if (msg.type == this.$enums.MESSAGE_TYPE.TEXT) return String(msg.content || "");
      if (msg.type == this.$enums.MESSAGE_TYPE.FILE) {
        try {
          const data = typeof msg.content === "object" ? msg.content : JSON.parse(msg.content || "{}");
          return (data && data.name) ? String(data.name) : "";
        } catch (e) { return ""; }
      }
      if (msg.type == this.$enums.MESSAGE_TYPE.RICH_TEXT || msg.type == this.$enums.MESSAGE_TYPE.MEDIA_ALBUM) {
        try {
          const data = typeof msg.content === "object" ? msg.content : JSON.parse(msg.content || "{}");
          if (data && Array.isArray(data.items) && data.items.length) {
            return String(data.caption || "");
          }
          return (Array.isArray(data.blocks) ? data.blocks : [])
            .filter(b => b && b.type === "text").map(b => b.content || "").join(" ");
        } catch (e) { return ""; }
      }
      return "";
    },
    buildSnippet(text, keyword) {
      const source = String(text || "");
      const lower = source.toLowerCase();
      const key = String(keyword || "").toLowerCase();
      const idx = lower.indexOf(key);
      if (idx < 0) return source.length > 80 ? source.slice(0, 80) + "..." : source;
      const start = Math.max(0, idx - 12);
      const end = Math.min(source.length, idx + key.length + 24);
      let snippet = source.slice(start, end);
      if (start > 0) snippet = "..." + snippet;
      if (end < source.length) snippet = snippet + "...";
      return snippet;
    },
    escapeHtml(text) {
      return String(text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    highlightText(text) {
      const safe = this.escapeHtml(text);
      if (!this.keyword) return safe;
      const kw = this.escapeHtml(this.keyword).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return safe.replace(new RegExp(`(${kw})`, "ig"), '<span class="kw">$1</span>');
    },
    async searchMessagesByKeyword(keyword) {
      if (!keyword) { this.messageResults = []; return; }
      try {
        const result = [];
        const conversations = this.chatStore.conversations;
        for (const conv of conversations) {
          if (conv.type === this.$enums.CONVERSATION_TYPE.SYSTEM) continue;
          const allMsgs = await this.$db.findMessageByConvKey(conv.key);
          let chatName;
          if (conv.type === this.$enums.CONVERSATION_TYPE.GROUP) {
            const group = this.groupStore.findGroup(conv.targetId);
            chatName = group ? group.showGroupName : (conv.showName || '群聊');
          } else {
            chatName = conv.showName || '私聊';
          }
          for (let i = allMsgs.length - 1; i >= 0; i--) {
            const msg = allMsgs[i];
            if (msg.deleted || msg.status == this.$enums.MESSAGE_STATUS.RECALL || msg.type == this.$enums.MESSAGE_TYPE.RECALL) continue;
            if (!this.messageMatchesKeyword(msg, keyword)) continue;
            const text = this.getSearchableText(msg);
            result.push({
              convKey: conv.key,
              localId: msg.localId,
              groupName: chatName,
              preview: this.buildSnippet(text, keyword)
            });
            if (result.length >= 30) {
              this.messageResults = result;
              return;
            }
          }
        }
        this.messageResults = result;
      } catch (e) {
        console.error('搜索消息失败', e);
        this.messageResults = [];
      }
    },
    openUser(id) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.$router.push(`/home/friend?id=${id}`);
    },
    openGroup(id) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.$router.push(`/home/group?id=${id}`);
    },
    openMessage(item) {
      this.showSearchPopup = false;
      this.clearSearchState();
      this.chatStore.pendingLocate = { convKey: item.convKey, localId: item.localId };
      this.chatStore.setActive(item.convKey);
    },
    // + 菜单
    togglePlusMenu() {
      this.showPlusMenu = !this.showPlusMenu;
    },
    onPlusMenuSelect(item) {
      this.showPlusMenu = false;
      switch (item.key) {
        case 'ADD_FRIEND':
          this.showAddFriend = true;
          break;
        case 'ADD_GROUP':
          this.showAddGroup = true;
          break;
        case 'CREATE_GROUP':
          this.onCreateGroup();
          break;
        case 'CHAT_LINK':
          this.openChatLink();
          break;
      }
    },
    // 创建群聊
    onCreateGroup() {
      this.$prompt('请输入群聊名称', '创建群聊', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputPattern: /\S/,
        inputErrorMessage: '请输入群聊名称',
        inputValidator: (value) => {
          if (value && value.length > 20) return '群聊名称不能超过20个字符';
        }
      }).then(({ value }) => {
        this.$http({
          url: `/group/create?groupName=${encodeURIComponent(value)}`,
          method: 'post',
          data: { name: value }
        }).then((group) => {
          this.groupStore.addGroup(group);
          this.$message.success('创建成功');
        });
      }).catch(() => {});
    },
    // 加入群聊
    onAddGroupSearch() {
      const keyword = (this.addGroupText || "").trim();
      if (!keyword) {
        this.$message.warning('请输入群号');
        return;
      }
      this.$http({
        url: `/group/search?groupNo=${encodeURIComponent(keyword)}`,
        method: 'get'
      }).then((data) => {
        this.addGroupResults = data || [];
        this.addGroupQuery = keyword;
        this.addGroupNoData = this.addGroupResults.length === 0;
      });
    },
    onApplyJoinGroup(group) {
      this.$http({
        url: '/group/request/applyBySearch',
        method: 'post',
        data: { groupId: group.id }
      }).then((joined) => {
        if (joined) {
          this.$message.success('加入成功');
          group.joined = true;
        } else {
          this.$message.info('申请已发送，等待审批');
          group.applied = true;
        }
      });
    },
    onAddGroupClosed() {
      this.addGroupText = "";
      this.addGroupQuery = "";
      this.addGroupResults = [];
      this.addGroupNoData = false;
    },
    // 聊天链接
    openChatLink() {
      const inviterId = this.userStore.userInfo.id;
      const baseUrl = `${window.location.origin}${window.location.pathname}`;
      const sep = baseUrl.includes('?') ? '&' : '?';
      this.chatLinkUrl = `${baseUrl}${sep}scene=chatLink&inviterId=${inviterId}`;
      this.showChatLink = true;
    },
    renderChatLinkQr() {
      this.$nextTick(() => {
        const canvas = this.$refs.chatLinkCanvas;
        if (canvas) {
          QRCode.toCanvas(canvas, this.chatLinkUrl, { width: 200, margin: 1 });
        }
      });
    },
    onSaveChatLinkQr() {
      const canvas = this.$refs.chatLinkCanvas;
      if (!canvas) return;
      const link = document.createElement('a');
      link.download = 'chat-link.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    },
    onCopyChatLink() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.chatLinkUrl).then(() => {
          this.$message.success('链接已复制');
        });
      } else {
        const input = document.createElement('input');
        input.value = this.chatLinkUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        this.$message.success('链接已复制');
      }
    },
  },
  computed: {
    activeConv() {
      return this.chatStore.activeConversation;
    },
    loading() {
      return this.chatStore.loading;
    },
    showConversations() {
      return this.chatStore.conversations;
    },
    keyword() {
      return (this.searchKeyword || "").trim().toLowerCase();
    },
    contactResults() {
      if (!this.keyword) return [];
      const key = this.keyword;
      return this.friendStore.friends
        .filter(f => !f.deleted)
        .filter(f => {
          const showName = String(f.showNickName || "").toLowerCase();
          const nickName = String(f.nickName || "").toLowerCase();
          const remarkName = String(f.remarkNickName || "").toLowerCase();
          return showName.includes(key) || nickName.includes(key) || remarkName.includes(key);
        })
        .slice(0, 10)
        .map(f => ({ id: f.id, showName: f.showNickName, headImage: f.headImage }));
    },
    groupResults() {
      if (!this.keyword) return [];
      const key = this.keyword;
      return this.groupStore.groups
        .filter(g => !g.quit)
        .map(g => {
          const matchInfo = this.groupMatchInfo(g, key);
          return { id: g.id, showName: g.showGroupName, includeText: matchInfo.includeText, matched: matchInfo.matched };
        })
        .filter(g => g.matched)
        .slice(0, 10);
    },
    plusMenuItems() {
      const items = [
        { key: 'ADD_GROUP', name: '加入群聊' },
        { key: 'CREATE_GROUP', name: '创建群聊' },
        { key: 'CHAT_LINK', name: '聊天链接' },
      ];
      // 仅 VIP 显示添加好友
      if (this.userStore.userInfo.isVip) {
        items.unshift({ key: 'ADD_FRIEND', name: '添加好友' });
      }
      return items;
    },
  }
}
</script>

<style lang="scss">
.chat-page {
  .header {
    flex-shrink: 0;
    height: auto;
    display: flex;
    align-items: center;
    padding: 12px 12px 10px;
    overflow: visible;
    gap: 6px;

    .search-wrap {
      position: relative;
      flex: 1;
      min-width: 0;
    }

    .search-popup {
      position: fixed;
      max-width: 460px;
      min-height: 320px;
      max-height: calc(100vh - 110px);
      overflow-y: auto;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.12);
      border-radius: 8px;
      box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
      z-index: 9999;

      .search-tip {
        padding: 20px;
        color: #999;
        font-size: 13px;
        text-align: center;
      }

      .search-section {
        border-bottom: 8px solid #f6f7fb;
        &:last-child { border-bottom: 0; }
      }

      .section-title {
        font-size: 13px;
        color: #999;
        padding: 12px 16px 8px;
        font-weight: 500;
      }

      .result-row {
        border-top: 1px solid #f2f2f2;
        cursor: pointer;
      }

      .chat-item-like {
        height: 54px;
        display: flex;
        position: relative;
        margin: 0 3px;
        padding: 5px 8px;
        align-items: center;
        white-space: nowrap;
        border-radius: 10px;

        &:hover { background-color: var(--im-background-active); }

        .chat-left {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          flex: 0 0 auto;
        }

        .chat-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding-left: 10px;
          text-align: left;
          overflow: hidden;
          min-width: 0;

          .chat-name {
            display: flex;
            align-items: center;
            line-height: 26px;
            height: 26px;

            .chat-name-text {
              font-size: var(--im-font-size);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              color: var(--im-text-color);
            }
          }

          .chat-content {
            display: flex;
            line-height: 24px;
            height: 24px;

            .chat-content-text {
              flex: 1;
              font-size: var(--im-font-size-smaller);
              color: var(--im-text-color-light);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }

      .kw {
        color: #e34d59;
        font-weight: 600;
      }
    }

    .plus-wrap {
      position: relative;
      flex-shrink: 0;

      .plus-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 30px;
        height: 30px;
        border-radius: 6px;
        border: 1px solid var(--im-border-color, #dcdfe6);
        background: transparent;
        cursor: pointer;
        color: var(--im-text-color-light, #909399);
        font-size: 16px;
        transition: background 0.15s, color 0.15s;

        &:hover {
          background: var(--im-background-active, #f0f2f5);
          color: var(--im-text-color, #303133);
        }
      }

      .plus-menu {
        position: absolute;
        top: calc(100% + 6px);
        right: 0;
        min-width: 120px;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 8px;
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
        z-index: 9999;
        padding: 4px 0;
        overflow: hidden;

        .plus-menu-item {
          padding: 9px 16px;
          font-size: 13px;
          color: var(--im-text-color, #303133);
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.12s;

          &:hover {
            background: var(--im-background-active, #f5f7fa);
          }

        }
      }
    }
  }

  .chat-loading {
    height: 50px;
    background-color: #eee;

    .el-icon-loading {
      font-size: 24px;
      color: var(--im-text-color-light);
    }

    .el-loading-text {
      color: var(--im-text-color-light);
    }
  }

  .scroll-box {
    flex: 1;
    min-height: 0;
    padding-top: 8px;

    :deep(.el-scrollbar) {
      height: 100%;
    }
  }
}

// 加入群聊弹窗
.add-group-search {
  margin-bottom: 16px;
}

.add-group-results {
  max-height: 320px;
  overflow-y: auto;
}

.add-group-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: 1px solid var(--im-border-color-lighter, #f2f2f2);

  &:last-child { border-bottom: 0; }

  .add-group-info {
    flex: 1;
    min-width: 0;
    text-align: left;

    .add-group-name {
      font-size: 14px;
      color: var(--im-text-color, #303133);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .add-group-no {
      font-size: 12px;
      color: var(--im-text-color-light, #909399);
      margin-top: 2px;
    }
  }

  .add-group-action {
    flex-shrink: 0;

    .status-tip {
      font-size: 12px;
      color: var(--im-text-color-light, #909399);
    }
  }
}

.add-group-empty {
  text-align: center;
  padding: 32px 0;
  color: #999;
  font-size: 13px;
}

// 聊天链接二维码弹窗
.chat-link-dialog {
  border-radius: 16px !important;
  overflow: hidden;

  .el-dialog__header { display: none; }
  .el-dialog__body { padding: 0; }
}

.chat-link-card {
  .clc-body {
    padding: 24px 24px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #fff;

    .clc-qr-wrap {
      padding: 12px;
      border-radius: 12px;
      border: 1px solid var(--im-border-color, #e4e7ed);
      box-shadow: 0 2px 10px rgba(65, 105, 222, 0.08);
      background: #fff;

      canvas { display: block; }
    }

    .clc-actions {
      display: flex;
      gap: 12px;
      margin-top: 20px;
      width: 100%;

      .clc-btn {
        flex: 1;
        height: 38px;
        border-radius: 19px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        border: none;
        transition: all 0.15s;

        &:active { transform: scale(0.97); opacity: 0.85; }

        &.outline {
          border: 1px solid var(--im-border-color, #dcdfe6);
          color: var(--im-text-color, #303133);
          background: #fff;

          &:hover { background: var(--im-background-active, #f0f2f5); }
        }

        &.primary {
          background: var(--im-gradient-bubble);
          color: #fff;
          box-shadow: var(--im-shadow-bubble);

          &:hover { opacity: 0.9; }
        }
      }
    }
  }
}
</style>
